import React, { useEffect, useState } from 'react';
import Amplify, { Auth, Logger } from 'aws-amplify';
import { SecuredVendorApp } from '@wfm-component-lib/auth';
import appConfig from './configuration/AppConfig';

import Dashboard from './components/Dashboard';

import './App.scss';
import '@wfm-component-lib/auth/dist/index.css';
import './index.css';

Amplify.Logger.LOG_LEVEL = appConfig.LOG_LEVEL || 'ERROR';
const logger = new Logger('App.js');

Amplify.configure(appConfig);
logger.debug('Amplify.configure', appConfig);

function App() {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  
  useEffect(() => {
    // Check if user is authenticated
    const checkAuth = async () => {
      try {
        const userData = await Auth.currentAuthenticatedUser();
        logger.debug('User authenticated:', userData);

        // Check if user is an external vendor who needs MFA
        const userGroups = userData.signInUserSession?.idToken?.payload['cognito:groups'] || [];
        const isExternalVendor = userGroups.includes('external-vendor');

        if (isExternalVendor) {
          // MFA completion is verified AUTHORITATIVELY against the server, not from a
          // client-controlled value. Previously this gate trusted
          // sessionStorage.getItem('mfaCompleted'), which any user could set in the
          // browser to skip MFA entirely (V2339031824 / Shepherd V2336686811). The
          // /check-mfa-code endpoint derives identity from the Cognito access token
          // server-side, so its mfaVerified answer is trustworthy. Fail closed: any
          // non-verified result or error redirects to /mfa.
          const email = userData.signInUserSession?.idToken?.payload?.email;
          if (email) {
            sessionStorage.setItem('userEmail', email);
          }

          let mfaVerified = false;
          try {
            const session = await Auth.currentSession();
            const accessToken = session.getAccessToken().getJwtToken();
            const resp = await fetch(process.env.REACT_APP_MFA_CHECK_URL, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({})
            });
            if (resp.ok) {
              const data = await resp.json();
              mfaVerified = data.mfaVerified === true;
            }
          } catch (err) {
            logger.error('MFA verification check failed; failing closed:', err);
            mfaVerified = false;
          }

          if (!mfaVerified) {
            logger.debug('External vendor not MFA-verified (server check), redirecting to MFA');
            // Clear any stale client-side flag so it can never act as an authority.
            sessionStorage.removeItem('mfaCompleted');
            window.location.href = '/mfa';
            return;
          }
        }
        logger.debug('User is fully authenticated');
        setIsCheckingAuth(false);
      } catch (error) {
        logger.error('Authentication error:', error);
        // If not authenticated, redirect to login page
        window.location.href = '/login';
      }
    };
    
    checkAuth();
  }, []);

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <SecuredVendorApp
      appName={appConfig.appName}
      requiredCognitoGroups={appConfig.requiredCognitoGroups}
      corporateIdEnabled={false}
    >
      <Dashboard />
    </SecuredVendorApp>
  );
};

export default App;
